import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Heart, Plus } from "lucide-react";
import { formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { toast } from "sonner";

export default function ProductCard({ product, idx = 0, aspect = "4/5" }) {
    const { addItem } = useCart();
    const { ids: wishlistIds, toggle } = useWishlist();
    const wished = wishlistIds.has(product.id);
    const totalStock = product.variants?.reduce((s, v) => s + Math.max(0, v.stock - (v.reserved || 0)), 0) || 0;
    const colors = (() => {
        const seen = new Set();
        const out = [];
        for (const v of product.variants || []) {
            const raw = (v.color || "").trim();
            const key = raw.toLowerCase();
            if (!raw || seen.has(key)) continue;
            seen.add(key);
            out.push(raw);
            if (out.length >= 4) break;
        }
        return out;
    })();
    const compareDiscount = product.compare_price && product.compare_price > product.price
        ? Math.round((1 - product.price / product.compare_price) * 100) : 0;

    const handleQuickAdd = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const firstAvail = product.variants?.find(v => v.stock - (v.reserved || 0) > 0);
        if (!firstAvail) {
            toast.error("Out of stock");
            return;
        }
        addItem(product.id, firstAvail.sku, 1);
    };

    const toggleWish = (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggle(product.id);
    };

    const aspectStyle = { aspectRatio: aspect.replace("/", " / ") };

    return (
        <motion.article
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.42, delay: Math.min(idx, 8) * 0.04, ease: [0.2, 0.8, 0.2, 1] }}
            className="group"
            data-testid="product-card"
        >
            <Link to={`/product/${product.slug}`} className="block" data-testid="product-card-image-link">
                <div
                    className="relative overflow-hidden rounded-2xl bg-secondary shadow-[0_8px_20px_rgba(0,0,0,0.3)] transition-all duration-500 group-hover:shadow-[0_30px_60px_rgba(224,60,74,0.28)] group-hover:-translate-y-1"
                    style={aspectStyle}
                >
                    {/* Badges (top-left column) */}
                    <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5">
                        {product.badges?.slice(0, 2).map(b => (
                            <span key={b} className="px-2.5 py-1 rounded-full bg-background/90 backdrop-blur-md border border-border text-[9.5px] font-mono-ui tracking-[0.14em]">
                                {b}
                            </span>
                        ))}
                        {compareDiscount > 0 && (
                            <span className="px-2.5 py-1 rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] text-[9.5px] font-mono-ui tracking-[0.14em]">
                                −{compareDiscount}%
                            </span>
                        )}
                        {totalStock === 0 ? (
                            <span className="px-2.5 py-1 rounded-full bg-foreground text-background text-[9.5px] font-mono-ui tracking-[0.14em]">
                                Sold out
                            </span>
                        ) : totalStock <= 5 && (
                            <span className="px-2.5 py-1 rounded-full bg-background/90 backdrop-blur-md border border-border text-[9.5px] font-mono-ui tracking-[0.14em] text-[hsl(var(--accent))]">
                                Low stock
                            </span>
                        )}
                    </div>

                    {/* Wishlist heart (top-right, always visible so mobile can tap it) */}
                    <button
                        onClick={toggleWish}
                        className={`absolute top-3 right-3 z-10 h-9 w-9 rounded-full backdrop-blur-md border flex items-center justify-center transition-colors ${
                            wished
                                ? "bg-[hsl(var(--accent))] border-transparent text-white"
                                : "bg-background/85 border-border hover:bg-background"
                        }`}
                        data-testid="product-card-wishlist-button"
                        aria-label={wished ? "Remove from wishlist" : "Save to wishlist"}
                        aria-pressed={wished}
                    >
                        <Heart className={`h-3.5 w-3.5 ${wished ? "fill-current" : ""}`} />
                    </button>

                    {/* Primary image */}
                    <img
                        src={product.images?.[0]}
                        alt={product.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:scale-[1.09]"
                    />
                    {/* Secondary image swap */}
                    {product.images?.[1] && (
                        <img
                            src={product.images[1]}
                            alt=""
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                        />
                    )}
                    {/* Red glow scrim on hover */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
                        background: "radial-gradient(400px 200px at 50% 100%, hsla(358, 82%, 44%, 0.35), transparent 70%)",
                    }} />
                    {/* Bottom gradient for legibility */}
                    <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400" />

                    {/* Quick add on hover */}
                    <div className="absolute inset-x-3 bottom-3 transform translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                        <button
                            onClick={handleQuickAdd}
                            className="w-full dw-btn-primary px-4 h-10 text-[11px] font-mono-ui inline-flex items-center justify-center gap-1.5"
                            data-testid="product-card-quick-add-button"
                            disabled={totalStock === 0}
                        >
                            <Plus className="h-3.5 w-3.5" /> Quick add
                        </button>
                    </div>
                </div>

                <div className="mt-3.5 px-0.5">
                    <p className="font-mono-ui text-muted-foreground truncate">{product.category}</p>
                    <div className="mt-1 flex items-start justify-between gap-3">
                        <h3 className="text-[14px] leading-tight line-clamp-2 flex-1" data-testid="product-card-title">
                            {product.title}
                        </h3>
                    </div>
                    <div className="mt-1.5 flex items-baseline gap-2">
                        <span className="font-mono-ui text-[13px] tracking-[0.08em] normal-case" data-testid="product-card-price">
                            {formatPrice(product.price)}
                        </span>
                        {product.compare_price && product.compare_price > product.price && (
                            <span className="text-xs text-muted-foreground line-through font-mono-ui tracking-[0.06em] normal-case">
                                {formatPrice(product.compare_price)}
                            </span>
                        )}
                    </div>
                    {colors.length > 0 && (
                        <div className="mt-2 flex items-center gap-1.5">
                            {colors.map(c => {
                                const hex = product.variants.find(v => (v.color || "").trim().toLowerCase() === c.toLowerCase())?.color_hex || "#000";
                                return (
                                    <span
                                        key={c}
                                        className="h-3 w-3 rounded-full border border-border ring-1 ring-inset ring-black/5"
                                        style={{ background: hex }}
                                        title={c}
                                    />
                                );
                            })}
                            {product.variants && product.variants.length > colors.length && (
                                <span className="text-[10px] text-muted-foreground ml-1 font-mono-ui">+{product.variants.length - colors.length}</span>
                            )}
                        </div>
                    )}
                </div>
            </Link>
        </motion.article>
    );
}
